/**
 * [입고관리 > 회송/폐기 관리] 코드값·뱃지 상수
 *
 * @note 필터 Select 옵션은 서버 제공값을 사용하고, 여기서는 목록 테이블이 의존하는 코드→명/뱃지 매핑만 유지한다.
 */

/** 처리방법 (inspectionProcessMethod) 코드 → 명 */
export const RETURN_DISPOSAL_PROCESS_METHOD = {
  APPLICATION_AWAITING: '신청대기',
  RTS: '회송',
  SCRAP: '폐기',
  CLAIM_REQUEST: '클레임신청',
  HELD: '보류',
  REPLACEMENT: '재입고',
} as const;

export type TypeReturnDisposalProcessMethod = keyof typeof RETURN_DISPOSAL_PROCESS_METHOD;

/** 처리상태 (inspectionProcessStatus) 코드 → 명 */
export const RETURN_DISPOSAL_PROCESS_STATUS = {
  PENDING_INPUT: '정보입력대기',
  IN_PROGRESS: '진행중',
  CLAIM_REQUESTED: '클레임신청',
  HELD: '보류',
  WAITING: '대기',
  COMPLETED: '완료',
} as const;

export type TypeReturnDisposalProcessStatus = keyof typeof RETURN_DISPOSAL_PROCESS_STATUS;

/** 처리상태 뱃지 컬러 (Kitchen Badge color 매핑) */
export const RETURN_DISPOSAL_PROCESS_STATUS_BADGE_COLOR = {
  PENDING_INPUT: 'info',
  IN_PROGRESS: 'secondary',
  CLAIM_REQUESTED: 'secondary',
  HELD: 'warning',
  WAITING: 'primary',
  COMPLETED: 'secondary',
} as const;

/** 처리방법(코드) → 노출 처리상태(코드) 매핑. 필터에서 처리방법 선택 시 해당 상태만 노출한다. */
export const RETURN_DISPOSAL_STATUS_BY_METHOD: Record<
  TypeReturnDisposalProcessMethod,
  TypeReturnDisposalProcessStatus[]
> = {
  APPLICATION_AWAITING: ['PENDING_INPUT'],
  RTS: ['PENDING_INPUT', 'IN_PROGRESS', 'COMPLETED'],
  SCRAP: ['IN_PROGRESS', 'COMPLETED'],
  CLAIM_REQUEST: ['CLAIM_REQUESTED'],
  HELD: ['HELD'],
  REPLACEMENT: ['WAITING', 'COMPLETED'],
};

/** 처리신청 매트릭스 액션 (회수는 직배·택배 공통 RTS 하나로 판정한다) */
export type TypeReturnDisposalMatrixAction = 'RTS' | 'SCRAP' | 'CLAIM';

type TypeReturnDisposalMatrixKey = `${TypeReturnDisposalProcessMethod}:${TypeReturnDisposalProcessStatus}`;

/**
 * 상태별 처리방법 전환 매트릭스 — `처리방법:처리상태` 조합에서 신청 가능한 액션 (포털 공통 정책)
 *
 * - 여기 없는 조합은 신청 불가로 판정한다.
 * - 폐기(진행중)·완료 조합은 행 선택 자체가 막혀 있지만(isRowSelectable) 정책 표와 1:1로 두기 위해 남긴다.
 */
export const RETURN_DISPOSAL_REQUESTABLE_ACTIONS: Partial<
  Record<TypeReturnDisposalMatrixKey, TypeReturnDisposalMatrixAction[]>
> = {
  'APPLICATION_AWAITING:PENDING_INPUT': ['RTS', 'SCRAP', 'CLAIM'],
  'RTS:IN_PROGRESS': ['SCRAP', 'CLAIM'],
  'RTS:PENDING_INPUT': ['RTS', 'SCRAP'],
  'SCRAP:IN_PROGRESS': [],
  'CLAIM_REQUEST:CLAIM_REQUESTED': ['RTS', 'SCRAP'],
  'HELD:HELD': [],
  'REPLACEMENT:WAITING': [],
  'RTS:COMPLETED': [],
  'SCRAP:COMPLETED': [],
  'REPLACEMENT:COMPLETED': [],
};

/** 처리신청 액션 (목록 상단 버튼) */
export const RETURN_DISPOSAL_REQUEST_ACTIONS = [
  { key: 'RTS_DIRECT', label: '회수(직배) 신청' },
  { key: 'RTS_PARCEL', label: '회수(택배) 신청' },
  { key: 'SCRAP', label: '폐기 신청' },
  { key: 'CLAIM', label: '클레임 신청' },
] as const;

export type TypeReturnDisposalRequestAction = (typeof RETURN_DISPOSAL_REQUEST_ACTIONS)[number]['key'];

/** 회수 방법 (operation-times 조회 / rts 신청 요청) */
export const RETURN_DISPOSAL_RTS_METHOD = {
  PARCEL: 'PARCEL',
  DIRECT: 'DIRECT',
} as const;

/** [회수(택배)] 택배사 코드 그룹 (`GET /codes?codeGroup=`) */
export const COURIER_CODE_GROUP = 'COURIER';

/** [회수(택배)] 송장번호 최대 길이 (영문·숫자·하이픈 포함) */
export const RTS_PARCEL_INVOICE_MAX_LENGTH = 20;

/** [회수(택배)] 송장번호 허용 형식 (영문·숫자·하이픈 8~20자, 하이픈만으로는 불가) */
export const RTS_PARCEL_INVOICE_PATTERN = /^(?=.*[A-Za-z0-9])[A-Za-z0-9-]{8,20}$/;

/** [회수(택배)] 이슈카드 1건당 등록 가능한 송장번호 최대 개수 (도달 시 추가 버튼 비활성) */
export const RTS_PARCEL_INVOICE_MAX_COUNT = 10;

/** [회수(택배)] 회수예정일 시간 표시 문구 — 택배는 시간 선택이 불필요해 종일 고정값만 노출한다 */
export const RTS_PARCEL_RECOVERY_TIME_TEXT = '00:00~23:59';

/** [회수(직배)] 렌탈사 카드 최대 개수 (KPP/AJU 2곳) */
export const RTS_DIRECT_MAX_RENTAL_CARDS = 2;

/** [회수(직배)] 반출 상세 주소 최대 길이 */
export const RTS_DIRECT_RELEASE_ADDRESS_MAX_LENGTH = 255;

/** [회수(직배)] 렌탈사 카드 업체코드 최대 길이 */
export const RTS_DIRECT_BUSINESS_CODE_MAX_LENGTH = 20;

/** [회수(직배)] 렌탈사 카드 담당자 성함 최대 길이 */
export const RTS_DIRECT_CONTACT_NAME_MAX_LENGTH = 20;

/** [회수(직배)] 렌탈사 카드 담당자 연락처 최대 길이 (하이픈 포함) */
export const RTS_DIRECT_CONTACT_PHONE_MAX_LENGTH = 15;

/** [회수(직배)] 렌탈사 카드 담당자 연락처 허용 형식 (숫자·하이픈 9~15자, 하이픈만으로는 불가) */
export const RTS_DIRECT_CONTACT_PHONE_PATTERN = /^(?=.*\d)[0-9-]{9,15}$/;

/** [회수(직배)] 파렛트 반출 여부 라디오 값 */
export const RTS_DIRECT_PALLET_RELEASE = {
  RELEASE: 'RELEASE',
  NONE: 'NONE',
} as const;

export type TypeRtsDirectPalletRelease = (typeof RTS_DIRECT_PALLET_RELEASE)[keyof typeof RTS_DIRECT_PALLET_RELEASE];

/** [회수(직배)] 파렛트 반출 여부 라디오 옵션 (기본: 반출) */
export const RTS_DIRECT_PALLET_RELEASE_OPTIONS = [
  { value: RTS_DIRECT_PALLET_RELEASE.RELEASE, label: '반출' },
  { value: RTS_DIRECT_PALLET_RELEASE.NONE, label: '미반출' },
] as const;

/** 처리신청 안내 문구 (버튼 툴팁·센터 상이 다이얼로그·성공 토스트) */
export const RETURN_DISPOSAL_REQUEST_MESSAGE = {
  // 기획 확정 문안만 노출한다. 기획에 없는 비활성 사유(회송(진행중)·보류·재입고)를 설명하는 보조 줄은 덧붙이지 않는다
  ACTION_TOOLTIP: [
    '이슈카드를 1건 이상 선택해야 신청 버튼이 활성화됩니다.',
    '처리완료 상태 및 폐기(진행중) 상태의 이슈카드는 선택이 불가합니다.',
    '[회수] 신청: 이슈발생센터와 입고요청번호가 모두 같은 이슈카드만 묶어서 신청 가능합니다.',
    '[폐기 신청]: 컬리나우 센터의 이슈카드는 신청이 불가합니다.',
    '[클레임 신청]: 이슈 등록일 D+3 23:59 이내, 클레임 이력이 없는 카드만 신청 가능합니다.',
  ].join('\n'),
  /** 선택 불가 행 체크박스 마우스오버 안내 — 처리완료·폐기(진행중) 사유별 (신청 버튼 [?]의 ACTION_TOOLTIP과 별개) */
  COMPLETED_ROW_TOOLTIP: '이미 처리가 완료된 상품입니다.',
  SCRAP_IN_PROGRESS_ROW_TOOLTIP: '폐기 처리 중인 상품은 처리방법을 변경할 수 없습니다.',
  CENTER_MISMATCH_TITLE: '처리 신청 불가 (이슈발생센터/발주 상이)',
  CENTER_MISMATCH_BODY:
    '이슈발생센터와 입고요청번호가 모두 동일한 상품들만 일괄 신청이 가능합니다. 입고요청별로 조회하여 각각 신청해 주세요.',
  RECOVERY_DATE_TOOLTIP: '선택하신 이슈카드 중 보관 기한이 임박한 건 기준으로\n회수예정일이 제한될 수 있습니다.',
  // 보류해제-회송 건 전용. 회수예정일이 컬리 지정일로 고정되어 변경 불가함을 안내한다
  RECOVERY_DATE_FIXED_TOOLTIP:
    '회수예정일은 컬리 담당자가 협의된 일정을 기준으로 지정합니다.\n변경이 필요한 경우 컬리 담당자에게 문의해 주세요.',
  // 차단 사유를 타이틀 괄호에 부기한다 (팝업이 여러 종이라 사유별로 구분되어야 한다)
  RTS_PENDING_INPUT_MIXED_TITLE: '처리 신청 불가 (회수예정일 지정 건 혼합)',
  RTS_PENDING_INPUT_MIXED_BODY:
    '회송(정보입력대기) 건은 컬리가 보류 해제 시 회수예정일을 지정한 건이라 날짜를 변경할 수 없습니다. 직접 날짜를 선택하는 건과 함께 신청할 수 없으니 나누어 각각 신청해 주세요.',
  RTS_PLANNED_AT_MISMATCH_TITLE: '처리 신청 불가 (회수예정일 상이)',
  RTS_PLANNED_AT_MISMATCH_BODY:
    '회송(정보입력대기) 상태는 회송예정일이 같은 경우만 함께 신청할 수 있습니다. 회송예정일이 다른 건을 선택 해제한 후 다시 신청해 주세요.',
  RTS_DIRECT_SUBMIT_SUCCESS: '신청되었습니다.',
  RTS_PARCEL_SUBMIT_SUCCESS: '신청되었습니다.',
  SCRAP_SUBMIT_SUCCESS: '신청되었습니다.',
  CLAIM_SUBMIT_SUCCESS: '신청되었습니다.',
  /** 송장번호 형식 오류 (영문·숫자·하이픈 8~20자) */
  INVOICE_FORMAT_ERROR: '영문/숫자/하이픈 8~20자로 입력해주세요.',
  /** 송장번호 케이스 A: 동일 신청(모달) 내 중복 */
  INVOICE_DUP_IN_MODAL: '동일 신청 내 이미 입력된 송장번호입니다.',
  /** 송장번호 케이스 B: 다른 이슈카드에 이미 등록된 택배사+송장번호 — 신청 실패 응답의 송장번호 목록을 해당 행에 표시 */
  INVOICE_DUP_IN_SYSTEM: '이미 다른 이슈카드에 등록된 송장번호입니다.',
  /** 회수(직배) 담당자 연락처 미입력 또는 형식 오류 (숫자·하이픈 9~15자) */
  CONTACT_PHONE_FORMAT_ERROR: '숫자와 하이픈(-) 포함 9~15자로 입력해 주세요',
} as const;

/**
 * [회수(택배)] 처리신청 모달 상단 '택배 회수 안내' 문구 (Figma 862:3821 기준)
 *
 * @note 회수장소 주소는 inbound-area 조회 결과를 노출, 미조회 시 '-'.
 */
export const RTS_PARCEL_NOTICE = {
  TITLE: '택배 회수 안내',
  NO_RECEIPT: '컬리는 별도의 배차 또는 택배 접수를 진행하고 있지 않습니다.',
  LOCATION_PREFIX: '회수장소: ',
  NO_DOCUMENT: '별도로 가져와야 하는 구비 서류는 없습니다.',
} as const;

/**
 * [회수(택배)] 반송장 작성 가이드 모달 (Figma 894:6092 / PRD §3.1)
 *
 * @note 본문(주의사항·안내·송장 예시·4단계 콜아웃)은 Figma 디자인을 그대로 반영한 단일 이미지로 노출한다.
 */
export const RETURN_INVOICE_GUIDE = {
  TITLE: '컬리(Kurly) 회송 물품 반송장 작성 가이드',
} as const;

/** [클레임] 신청 기한(이슈 등록일 D+N일). elapsedDays가 초과하면 [클레임 신청] 버튼을 비활성화한다. */
export const CLAIM_DEADLINE_DAYS = 3;

/** [클레임] 거래명세서 이미지 업로드 제약 */
export const CLAIM_IMAGE = {
  /** 최대 등록 장수 */
  MAX_COUNT: 30,
  /** 개별 파일 최대 용량 (8MB) */
  MAX_FILE_SIZE: 8 * 1024 * 1024,
  /** 전체 파일 합계 최대 용량 (50MB) */
  MAX_TOTAL_SIZE: 50 * 1024 * 1024,
  /** input accept 속성 (JPG/PNG) */
  ACCEPT: 'image/jpeg, image/jpg, image/png',
  /** 허용 MIME 타입 */
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png'],
} as const;

/** [클레임] 사유 입력 최대 길이 */
export const CLAIM_REASON_MAX_LENGTH = 500;

/** [클레임] 처리신청 모달 문구 (안내·검증 에러) */
export const CLAIM_NOTICE = {
  /** 상단 안내 패널 제목 */
  GUIDE_TITLE: '클레임 신청 안내',
  /** 상단 안내 패널 - 신청 기한 */
  GUIDE_DEADLINE: '신청 기한: 이슈 등록일 기준 D+3 23:59까지',
  /** 상단 안내 패널 - 1회 제한 */
  GUIDE_ONCE: '이슈사유별로 1회 제한',
  /** 사유 입력 placeholder */
  REASON_PLACEHOLDER: '클레임 사유를 상세히 입력해 주세요.',
  /** 거래명세서 이미지 안내 (서명 포함 실물) */
  IMAGE_GUIDE_SIGNATURE: '컬리 작업자의 서명이 포함된 거래명세서 실물 사진을 첨부해 주세요.',
  /** 거래명세서 이미지 안내 (허용 형식·개수·용량 한 줄) */
  IMAGE_GUIDE_CONSTRAINT: `JPG, PNG 파일만 등록 가능합니다. (최대 ${CLAIM_IMAGE.MAX_COUNT}장, 개별 ${
    CLAIM_IMAGE.MAX_FILE_SIZE / (1024 * 1024)
  }MB · 총 ${CLAIM_IMAGE.MAX_TOTAL_SIZE / (1024 * 1024)}MB)`,
  /** 검증: 허용하지 않는 형식 */
  ERROR_TYPE: 'JPG, PNG 파일만 등록 가능합니다.',
  /** 검증: 개별 용량 초과 */
  ERROR_FILE_SIZE: `개별 파일은 ${CLAIM_IMAGE.MAX_FILE_SIZE / (1024 * 1024)}MB 이하만 등록할 수 있습니다.`,
  /** 검증: 총 용량 초과 */
  ERROR_TOTAL_SIZE: `이미지 총 용량은 ${CLAIM_IMAGE.MAX_TOTAL_SIZE / (1024 * 1024)}MB를 초과할 수 없습니다.`,
  /** 검증: 개수 초과 */
  ERROR_MAX_COUNT: `이미지는 최대 ${CLAIM_IMAGE.MAX_COUNT}장까지 등록할 수 있습니다.`,
} as const;

/** [폐기] 폐기 비용 산정 단가 (원/kg) */
export const SCRAP_UNIT_PRICE = 480;

/**
 * [폐기] 처리신청 모달 상단 '폐기 비용 안내' 문구
 *
 * @note 총 폐기물량({N}kg)·예상 비용({금액}원)은 선택 이슈카드 기준으로 계산해 치환한다.
 *       폐기중량 = Σ(net_weight kg × 이슈수량, 소수 셋째자리 반올림) / 비용 = 폐기중량 × 480원(소수점 버림).
 */
export const SCRAP_NOTICE = {
  TITLE: '폐기 비용 안내',
  CHARGE:
    '폐기 처리 시 발생하는 비용은 공급사에 청구됩니다. 폐기를 선택하시면 해당 상품은 폐기 처리되며, 이후 회수할 수 없습니다.',
  /** `{weight}`·`{fee}` 치환 */
  ESTIMATE: '폐기 물량 총 {weight}kg에 대해 약 {fee}원의 폐기 비용이 청구될 예정입니다.',
} as const;
